import { Globe } from "lucide-react";
import { FaXTwitter, FaLinkedinIn, FaGithub, FaYoutube, FaInstagram } from "react-icons/fa6";
import { Input } from "@/components/ui/Input";

type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

const PLATFORMS: { key: string; label: string; icon: IconComponent }[] = [
  { key: "twitter", label: "Twitter / X", icon: FaXTwitter },
  { key: "linkedin", label: "LinkedIn", icon: FaLinkedinIn },
  { key: "github", label: "GitHub", icon: FaGithub },
  { key: "youtube", label: "YouTube", icon: FaYoutube },
  { key: "instagram", label: "Instagram", icon: FaInstagram },
  { key: "website", label: "Website", icon: Globe },
];

interface SocialLinksEditorProps {
  links: { platform: string; url: string }[];
  onChange: (links: { platform: string; url: string }[]) => void;
}

export function SocialLinksEditor({ links, onChange }: SocialLinksEditorProps) {
  const usedPlatforms = new Set(links.map((l) => l.platform));
  const availablePlatforms = PLATFORMS.filter((p) => !usedPlatforms.has(p.key));

  const updateLink = (index: number, field: "platform" | "url", value: string) => {
    const updated = [...links];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeLink = (index: number) => {
    onChange(links.filter((_, i) => i !== index));
  };

  const addLink = () => {
    if (availablePlatforms.length === 0) return;
    onChange([...links, { platform: availablePlatforms[0].key, url: "" }]);
  };

  const getIcon = (key: string) => {
    const p = PLATFORMS.find((p) => p.key === key);
    return p ? p.icon : Globe;
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-body font-bold text-grey">
        Social Links
      </label>
      {links.map((link, idx) => {
        const Icon = getIcon(link.platform);
        return (
          <div key={idx} className="flex items-center gap-2">
            <div className="relative">
              <Icon
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-grey pointer-events-none"
              />
              <select
                value={link.platform}
                onChange={(e) => updateLink(idx, "platform", e.target.value)}
                className="pl-8 pr-2 py-2 rounded-lg border border-beige/40 bg-white font-body text-sm focus:outline-none focus:ring-2 focus:ring-orange/50 w-40 appearance-none"
              >
                {PLATFORMS.filter(
                  (p) => p.key === link.platform || !usedPlatforms.has(p.key),
                ).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              value={link.url}
              onChange={(e) => updateLink(idx, "url", e.target.value)}
              placeholder="https://..."
              className="flex-1 min-w-[300px]"
            />
            <button
              type="button"
              onClick={() => removeLink(idx)}
              className="text-grey hover:text-red-500 text-lg px-1 shrink-0"
              title="Remove"
            >
              &times;
            </button>
          </div>
        );
      })}
      {availablePlatforms.length > 0 && (
        <button
          type="button"
          onClick={addLink}
          className="text-sm text-orange hover:text-orange/80 font-body self-start"
        >
          + Add social link
        </button>
      )}
    </div>
  );
}
